import { useQuery } from '@apollo/client';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  OverlayDrawer,
  Spinner,
} from '@fluentui/react-components';
import { Copy24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import React from 'react';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import useToast from '../../../utils/hooks/useToast';
import { GET_DOCUMENT_EXTRACTED_TEXT } from '../../../utils/statApi/TextExtractionApi';
import { trackWarning } from '../../../utils/telemetry/appInsights';

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
  },
  title: {
    overflowWrap: 'anywhere',
  },
  textContent: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
});

interface IExtractedTextPanelProps {
  open: boolean;
  companyCode: string;
  documentId: number;
  documentTitle: string;
  onClose: () => void;
}

const ExtractedTextPanel = ({
  open,
  companyCode,
  documentId,
  documentTitle,
  onClose,
}: IExtractedTextPanelProps): JSX.Element => {
  const styles = useStyles();
  const { data, loading, error } = useQuery(GET_DOCUMENT_EXTRACTED_TEXT, {
    variables: { companyCode, documentId },
    skip: !open,
    fetchPolicy: 'network-only',
  });
  const { createToast } = useToast();
  const extractedText: string | undefined = data?.documentExtractedText;

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(extractedText);
      createToast({
        id: Math.random(),
        header: 'Text Copied',
        content: 'The extracted text was copied to your clipboard.',
        iconName: 'Info',
      });
    } catch (copyError) {
      trackWarning('Copying extracted text to the clipboard failed', {
        documentId,
        errorName: copyError instanceof Error ? copyError.name : typeof copyError,
        errorMessage: copyError instanceof Error ? copyError.message : String(copyError),
      });
      createToast({
        id: Math.random(),
        header: 'Copy Error',
        content:
          'The text could not be copied. You can select it in the panel and copy it manually.',
        iconName: 'ErrorBadge',
      });
    }
  };

  return (
    <OverlayDrawer
      aria-label={`Extracted text: ${documentTitle}`}
      modalType="modal"
      size="medium"
      position="end"
      open={open}
      onOpenChange={(_event, openData): void => {
        if (!openData.open) {
          onClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          heading={{ className: styles.title }}
          action={
            <div className={styles.actions}>
              <Button
                appearance="subtle"
                aria-label="Copy extracted text"
                disabled={loading || !extractedText}
                icon={<Copy24Regular />}
                onClick={onCopy}
              >
                Copy
              </Button>
              <Button
                appearance="subtle"
                aria-label="Close extracted text panel"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            </div>
          }
        >
          {documentTitle}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {loading && <Spinner label="Loading extracted text..." />}
        <LoadingErrorMessage loading={loading} error={error} />
        {!loading && !error && (
          <pre className={styles.textContent}>
            {extractedText || 'No extracted text available.'}
          </pre>
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
};

export default ExtractedTextPanel;
